import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  ButtonGroup,
  Button,
  IconButton
} from '@mui/material';
import styled from '@emotion/styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Styled components
const ProjectsSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'url("/assets/images/bg/portfolio-bg.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: '100px 0',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: '20px',
  fontWeight: 700,
  '&:after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    height: '3px',
    width: '50px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const FilterButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: '40px',
  gap: '10px',
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
  }
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  color: active ? '#ffffff' : '#333333',
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  borderRadius: '0',
  padding: '8px 20px',
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '0.95rem',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.main : 'rgba(0,0,0,0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 12px',
    fontSize: '0.85rem',
    minWidth: 'auto',
  }
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: '5px',
  overflow: 'hidden',
  boxShadow: 'none',
  height: '100%',
  '&:hover .project-content': {
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
  }
}));

const ProjectImage = styled(CardMedia)(({ theme }) => ({
  height: '300px',
  position: 'relative',
}));

const ProjectContent = styled(CardContent)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  color: '#ffffff',
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.1s ease, transform 0.1s ease',
  padding: '30px',
  textAlign: 'left',
}));

const ProjectSubtitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontSize: '0.9rem',
  fontWeight: 400,
  marginBottom: '10px',
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '1.5rem',
  lineHeight: 1.2,
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  position: 'absolute',
  bottom: '30px',
  left: '30px',
  width: '45px',
  height: '45px',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  width: '100%',
  '.slick-slide': {
    padding: '10px',
  },
  '.slick-dots': {
    bottom: '-40px',
    '& li button:before': {
      fontSize: '12px',
    },
    '& li.slick-active button:before': {
      color: theme.palette.primary.main,
    }
  },
  '.slick-prev, .slick-next': {
    zIndex: 1,
    '&:before': {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    }
  }
}));

const SliderArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  }
}));

const PrevArrow = styled(SliderArrow)(({ theme }) => ({
  left: '-50px',
}));

const NextArrow = styled(SliderArrow)(({ theme }) => ({
  right: '-50px',
}));

// Custom arrow components for the slider
const PrevArrowComponent = (props) => {
  const { onClick } = props;
  return (
    <PrevArrow onClick={onClick}>
      <ArrowBackIcon />
    </PrevArrow>
  );
};

const NextArrowComponent = (props) => {
  const { onClick } = props;
  return (
    <NextArrow onClick={onClick}>
      <ArrowForwardIcon />
    </NextArrow>
  );
};

function Projects() {
  const [filter, setFilter] = useState('all');
  
  // Define projects array first before using it
  const projects = [
    {
      id: 1,
      image: '/assets/images/project/style1/1.jpg',
      title: 'Enhanced Security Architecture',
      subtitle: 'Leveraging Google Cloud for Banking',
      category: 'security',
      link: 'https://hkmsp.com/gcloud_enhanced_security_architecture.html'
    },
    {
      id: 2,
      image: '/assets/images/project/style1/2.jpg',
      title: 'Clear Policies drove efficiency',
      subtitle: 'Cyber Uplift Program',
      category: 'compliance',
      link: 'https://hkmsp.com/cyber_uplift_program_clear_policies.html'
    },
    {
      id: 3,
      image: '/assets/images/project/style1/3.jpg',
      title: 'Setting the direction early',
      subtitle: 'Cyber Uplift Program',
      category: 'security',
      link: 'https://hkmsp.com/cyber_uplift_program_set_direction.html'
    },
    {
      id: 4,
      image: '/assets/images/project/style1/4.jpg',
      title: 'Authentication Outsourcing',
      subtitle: 'Secure Authentication Solutions',
      category: 'development',
      link: 'https://hkmsp.com/firebase_authentication_advantages.html'
    },
    {
      id: 5,
      image: '/assets/images/project/style1/5.jpg',
      title: 'Cybersecurity Awareness',
      subtitle: 'Cybersecurity Education',
      category: 'security',
      link: 'https://hkmsp.com/cybersecurity-awareness-program.html'
    },
    {
      id: 6,
      image: '/assets/images/project/style1/6.jpg',
      title: 'PCI DSS Compliance',
      subtitle: 'Secure Payment Environments',
      category: 'payments',
      link: 'https://hkmsp.com/pci_dss_compliance.html'
    }
  ];
  
  // Get filtered projects based on selected filter
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);
    
  // Adjust slider settings based on number of filtered projects
  const slidesToShow = Math.min(3, filteredProjects.length);
  const slidesToShowTablet = Math.min(2, filteredProjects.length);
  const infiniteMode = filteredProjects.length > 1;
  
  // Slider settings for the projects carousel
  const settings = {
    dots: true,
    infinite: infiniteMode,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: infiniteMode,
    autoplaySpeed: 5000,
    prevArrow: filteredProjects.length > 1 ? <PrevArrowComponent /> : null,
    nextArrow: filteredProjects.length > 1 ? <NextArrowComponent /> : null,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: slidesToShowTablet,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };
  
  // This is now handled above when creating the slider settings

  return (
    <ProjectsSection id="rs-project">
      <Container maxWidth="lg">
        <SectionTitle>
          <Title variant="h2">
            Completed business cases
          </Title>
        </SectionTitle>

        <FilterButtons>
          <FilterButton 
            variant={filter === 'all' ? 'contained' : 'text'}
            active={filter === 'all' ? 1 : 0} 
            onClick={() => setFilter('all')}
            disableElevation
          >
            All
          </FilterButton>
          <FilterButton 
            variant={filter === 'security' ? 'contained' : 'text'}
            active={filter === 'security' ? 1 : 0} 
            onClick={() => setFilter('security')}
            disableElevation
          >
            Banking on Google Cloud
          </FilterButton>
          <FilterButton 
            variant={filter === 'compliance' ? 'contained' : 'text'}
            active={filter === 'compliance' ? 1 : 0} 
            onClick={() => setFilter('compliance')}
            disableElevation
          >
            Cyber Uplift Program
          </FilterButton>
          <FilterButton 
            variant={filter === 'development' ? 'contained' : 'text'}
            active={filter === 'development' ? 1 : 0} 
            onClick={() => setFilter('development')}
            disableElevation
          >
            Cybersecurity Awareness
          </FilterButton>
          <FilterButton 
            variant={filter === 'payments' ? 'contained' : 'text'}
            active={filter === 'payments' ? 1 : 0} 
            onClick={() => setFilter('payments')}
            disableElevation
          >
            Secure Payments
          </FilterButton>
        </FilterButtons>

        <SliderContainer>
          <Slider {...settings}>
            {filteredProjects.map((project) => (
              <Box key={project.id} sx={{ padding: '10px' }}>
                <ProjectCard onClick={() => window.open(project.link, '_blank')} sx={{ cursor: 'pointer' }}>
                  <ProjectImage
                    image={project.image}
                    title={project.title}
                  />
                  <ProjectContent className="project-content">
                    <ProjectSubtitle>
                      {project.subtitle}
                    </ProjectSubtitle>
                    <ProjectTitle variant="h5">
                      {project.title}
                    </ProjectTitle>
                    <ArrowButton size="small">
                      <ArrowForwardIcon />
                    </ArrowButton>
                  </ProjectContent>
                </ProjectCard>
              </Box>
            ))}
          </Slider>
        </SliderContainer>
      </Container>
    </ProjectsSection>
  );
}

export default Projects;
